import { useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { Button, Input, Label } from '@alertwhale/ui'
import { useResetPassword } from '@/hooks/useAuth'
import { Lock, Loader2, CheckCircle } from 'lucide-react'

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [success, setSuccess] = useState(false)
  const resetPassword = useResetPassword()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password !== confirmPassword) return
    if (!token) return

    await resetPassword.mutateAsync({ token, password })
    setSuccess(true)
    setTimeout(() => navigate('/login'), 3000)
  }

  if (!token) {
    return (
      <div className="p-8">
        <div className="space-y-2 text-center mb-8">
          <h1 className="text-2xl font-bold text-white">Invalid link</h1>
          <p className="text-slate-400">
            This password reset link is invalid or has expired.
          </p>
        </div>
        <Link to="/forgot-password">
          <Button className="w-full bg-whale-500 hover:bg-whale-600 text-white font-medium">
            Request a new link
          </Button>
        </Link>
      </div>
    )
  }

  if (success) {
    return (
      <div className="p-8">
        <div className="space-y-4 text-center">
          <div className="mx-auto w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Password reset</h1>
          <p className="text-slate-400">
            Your password has been reset successfully. Redirecting to login...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="space-y-2 text-center mb-8">
        <h1 className="text-2xl font-bold text-white">Reset password</h1>
        <p className="text-slate-400">Enter your new password below</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="password" className="text-slate-300">New password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="password"
              type="password"
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-whale-500 focus:ring-whale-500/20"
              required
              minLength={8}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-slate-300">Confirm password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-whale-500 focus:ring-whale-500/20"
              required
            />
          </div>
          {password && confirmPassword && password !== confirmPassword && (
            <p className="text-xs text-red-400">Passwords don't match</p>
          )}
        </div>

        {resetPassword.error && (
          <div className="text-sm text-red-400">
            {resetPassword.error.message}
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-whale-500 hover:bg-whale-600 text-white font-medium"
          disabled={resetPassword.isPending || password !== confirmPassword}
        >
          {resetPassword.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Reset password
        </Button>
      </form>
    </div>
  )
}
